import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useClients, useCreateClient } from '@/features/clients/hooks/useClients'
import { ClientForm } from '@/features/clients/components/ClientForm'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { DataTable } from '@/components/data-table/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Plus } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/format'
import type { ClientFormData } from '@/features/clients/schemas/clientSchema'

export function ClientsListPage() {
  const navigate = useNavigate()
  const { data: clients, isLoading } = useClients()
  const createClient = useCreateClient()
  const [showCreateModal, setShowCreateModal] = useState(false)

  const handleCreate = async (data: ClientFormData) => {
    await createClient.mutateAsync(data as any)
    setShowCreateModal(false)
  }

  const columns = [
    { key: 'name', header: 'Client Name' },
    {
      key: 'baseline_ar_total',
      header: 'Starting AR',
      render: (client: any) => formatCurrency(client.baseline_ar_total)
    },
    { 
      key: 'contract_start_date', 
      header: 'Contract Start',
      render: (client: any) => formatDate(client.contract_start_date)
    },
    { 
      key: 'implementation_fee', 
      header: 'Implementation Fee',
      render: (client: any) => formatCurrency(client.implementation_fee)
    },
    {
      key: 'status',
      header: 'Status',
      render: (client: any) => (
        <Badge variant={client.status === 'active' ? 'success' : 'default'}>
          {client.status}
        </Badge>
      ),
    },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Client
        </Button>
      </div>

      <Card>
        <DataTable
          data={clients || []}
          columns={columns}
          onRowClick={(client) => navigate(`/clients/${client.id}`)}
          loading={isLoading}
          emptyMessage="No clients yet. Create your first client to get started."
        />
      </Card>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Client"
        size="lg"
      >
        <ClientForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          loading={createClient.isPending}
        />
      </Modal>
    </div>
  )
}
